import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import AlertMessage from "../components/AlertMessage";
import AnimatedPage from "../components/AnimatedPage";
import CountryPhoneInput from "../components/CountryPhoneInput";
import { User, Mail, Phone, Shield, Edit3, Save, X, Lock, Eye, EyeOff } from "lucide-react";

export default function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const [profileForm, setProfileForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    password: "",
    password_confirmation: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name,
        email: user.email,
        phone: user.phone || "",
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setProfileForm({ ...profileForm, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (phoneValue) => {
    setProfileForm({ ...profileForm, phone: phoneValue });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    const result = await updateProfile(profileForm);

    setIsLoading(false);

    if (result.success) {
      setSuccess("Profile updated successfully!");
      setIsEditing(false);
    } else {
      setError(result.error || "Failed to update profile.");
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (passwordForm.password.length < 8) {
      setPasswordError("Password must be at least 8 characters.");
      return;
    }
    if (passwordForm.password !== passwordForm.password_confirmation) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setIsPasswordLoading(true);
    const result = await updateProfile({
      password: passwordForm.password,
      password_confirmation: passwordForm.password_confirmation,
    });
    setIsPasswordLoading(false);

    if (result.success) {
      setPasswordSuccess("Password changed successfully!");
      setPasswordForm({ password: "", password_confirmation: "" });
      setIsChangingPassword(false);
      setShowPassword(false);
      setShowConfirmPassword(false);
    } else {
      setPasswordError(result.error || "Failed to change password.");
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError("");
    if (user) {
      setProfileForm({
        name: user.name,
        email: user.email,
        phone: user.phone || "",
      });
    }
  };

  if (!user) {
    return (
      <AnimatedPage>
        <div className="text-center py-10">
          <h1 className="text-2xl font-bold">Loading profile...</h1>
        </div>
      </AnimatedPage>
    );
  }

  // Get initials for avatar
  const initials = user.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  return (
    <AnimatedPage>
      <div className="mx-auto w-full max-w-lg space-y-6">
        {error && (
          <AlertMessage type="error" onClose={() => setError("")}>
            {error}
          </AlertMessage>
        )}
        {success && (
          <AlertMessage type="success" onClose={() => setSuccess("")}>
            {success}
          </AlertMessage>
        )}

        {/* Profile Header Card */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-xl font-bold text-white shadow-lg">
              {initials}
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                {user.name}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {user.email}
              </p>
              <span className="mt-1 inline-block rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                {user.role}
              </span>
            </div>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <Edit3 size={16} />
                Edit
              </button>
            )}
          </div>
        </div>

        {/* Profile Details / Edit Form */}
        <div className="rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          {isEditing ? (
            <form onSubmit={handleUpdate} className="p-6 space-y-5">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Edit Profile
              </h2>

              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  Full Name
                </label>
                <div className="relative">
                  <User
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    id="name"
                    name="name"
                    value={profileForm.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    required
                    className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-4 text-sm transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  Email Address
                </label>
                <div className="relative">
                  <Mail
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={profileForm.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    required
                    className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-4 text-sm transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Phone Number
                </label>
                <CountryPhoneInput
                  value={profileForm.phone}
                  onChange={handlePhoneChange}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Save size={16} />
                  {isLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-slate-800">
              {/* Name */}
              <div className="flex items-center gap-4 px-6 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800">
                  <User size={18} className="text-slate-600 dark:text-slate-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    Full Name
                  </p>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {user.name}
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center gap-4 px-6 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800">
                  <Mail size={18} className="text-slate-600 dark:text-slate-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    Email Address
                  </p>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {user.email}
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-4 px-6 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800">
                  <Phone size={18} className="text-slate-600 dark:text-slate-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    Phone Number
                  </p>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {user.phone || (
                      <span className="text-slate-400 dark:text-slate-500">
                        Not set -{" "}
                        <button
                          onClick={() => setIsEditing(true)}
                          className="text-emerald-600 hover:underline dark:text-emerald-400"
                        >
                          add phone
                        </button>
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Role */}
              <div className="flex items-center gap-4 px-6 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800">
                  <Shield size={18} className="text-slate-600 dark:text-slate-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    Account Role
                  </p>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100 capitalize">
                    {user.role}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Password Change Section */}
        <div className="rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          {passwordError && (
            <div className="px-6 pt-4">
              <AlertMessage type="error" onClose={() => setPasswordError("")}>
                {passwordError}
              </AlertMessage>
            </div>
          )}
          {passwordSuccess && (
            <div className="px-6 pt-4">
              <AlertMessage type="success" onClose={() => setPasswordSuccess("")}>
                {passwordSuccess}
              </AlertMessage>
            </div>
          )}

          {isChangingPassword ? (
            <form onSubmit={handlePasswordChange} className="p-6 space-y-5">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Lock size={20} />
                Change Password
              </h2>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  New Password
                </label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={passwordForm.password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                    className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-12 text-sm transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordForm.password_confirmation}
                    onChange={(e) => setPasswordForm({ ...passwordForm, password_confirmation: e.target.value })}
                    placeholder="Repeat your new password"
                    required
                    minLength={8}
                    className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-12 text-sm transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsChangingPassword(false);
                    setPasswordForm({ password: "", password_confirmation: "" });
                    setPasswordError("");
                    setShowPassword(false);
                    setShowConfirmPassword(false);
                  }}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isPasswordLoading}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Lock size={16} />
                  {isPasswordLoading ? "Changing..." : "Change Password"}
                </button>
              </div>
            </form>
          ) : (
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800">
                  <Lock size={18} className="text-slate-600 dark:text-slate-400" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Password</p>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">••••••••</p>
                </div>
              </div>
              <button
                onClick={() => setIsChangingPassword(true)}
                className="flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <Edit3 size={16} />
                Change
              </button>
            </div>
          )}
        </div>
      </div>
    </AnimatedPage>
  );
}
